import type {MasteryLevel} from "./mastery-types.ts";
import type {ItemStatus} from "./status-types.ts";

// Path to a learnable content item topic, as returned by published entity query.
interface TopicPath {
    path: Array<{
        id: string;
        kind: string;
        slug: string;
    }>;
}

export type DomainSlug =
    | "math"
    | "partner-content"
    | "computing"
    | "economics-finance-domain"
    | "gtp"
    | "humanities"
    | "science"
    | "test-prep"
    | "college-careers-more"
    | "college-admissions"
    | "default";

// This map of domain slugs is used to ensure that we can get a valid
// DomainSlug from a random string (see getValidDomainSlug)
export const domainSlugMap: Record<DomainSlug, DomainSlug> = {
    math: "math",
    "partner-content": "partner-content",
    computing: "computing",
    "economics-finance-domain": "economics-finance-domain",
    gtp: "gtp",
    humanities: "humanities",
    science: "science",
    "test-prep": "test-prep",
    "college-careers-more": "college-careers-more",
    "college-admissions": "college-careers-more",
    default: "default",
};

export type DomainId = string;
export type CourseId = string;
export type Unit = string;
export type UnitId = string;
export type LessonId = string;
export type ContentId = string;

// TODO (jsatk): eventually dedupe with and merge into type QuizTutorial
export interface Tutorial {
    id: string;
    title: string;
    slug: string;
    // TODO(kevinb): figure out why Array<Item> doesn't work
    items: Array<any>;
}

export interface TutorialT {
    title: string;
    slug: string;
    id: string;
    contentItems: Array<ContentItem>;
    masteryPercentage?: number;
}

export interface Topic {
    icon: string;
    slug: string;
    title: string;
    tutorials: Array<Tutorial>;
    url: string;
}

export interface NextTopic {
    previous?: {
        title: string;
        url: string;
    };
    next?: {
        title: string;
        url: string;
    };
}

export type RouteParam = [
    // article slug
    // current tab
    (
        | "t" // exercise slug
        | "e" // video slug
        | "v"
        | "a"
    ),
    string,
];

// sync-start:standards 2043036881 ./libs/standards/ui/src/types.ts
// TODO(djf): The coach dashboard code is currently passing descriptionHtml
// and link arguments, but the code in content_render/handlers.py is not.
// The <Standards> component in standards-package/ can work either way.
// When we've fully switched away from hardcoded common-core standards
// in the coach dashboard, we should remove descriptionHtml and link.
export interface Standard {
    code: string;
    descriptionHtml?: string;
    link?: string;
    set: {
        id?: string;
        isDiscoverable: boolean;
        prefix: string;
    };
}
// sync-end:standards

export interface ContentNodePath {
    domainId?: string;
    courseId?: string;
    unitId?: string;
    lessonId?: string;
    lessonSlug?: string;
    specificUrlPath?: string | null | undefined;
}

// NOTE(tony): Consider auditing these fields, since they're not always
// present in every content item kind nor are they all used.
export interface CommonContentItem {
    contentId: string;
    // TODO(kevinb) think about making this optional
    progressKey: string;
    title: string;
    description: string;
    thumbnailUrl: string;
    nodeUrl: string;
    nodePath?: ContentNodePath;
    // TODO(kevinb) think about making this optional
    status: ItemStatus;
    slug: string;
    // The ID and slug of the deepest known parent topic, preferably the parent
    // tutorial. Used for analytics purposes.
    topicId: string;
    topicPaths?: Array<TopicPath>;
    topicSlug: string;
    sponsored: boolean;
    recommended: boolean;
    mappedStandardIds?: Array<string>;
}

export interface EndOfVideoAttributionType {
    logoCaption: string;
    logoUrl: string;
}

export type Video = CommonContentItem & {
    kind: "Video";
    youtubeId: string;
    translatedYoutubeId: string;
    translatedYoutubeLang: string;
    duration?: number;
    downloadUrls: Record<string, string>;
    // Optional sponsor attribution to show at the end of video. This is
    // inserted as an additional prop by `topic-progress.jsx` from provided
    // TopicProgress module props.
    attribution?: EndOfVideoAttributionType;
};

export interface TimeEstimateT {
    lowerBound: number;
    upperBound: number;
}

export interface Score {
    numCorrect: number;
    numAttempted: number;
    // An ISO 8601 datetime, e.g.: "2017-06-26T16:39:25.437492+00:00".
    completedDate: string;
}

export type Exercise = CommonContentItem & {
    kind: "Exercise";
    isSkillCheck: boolean;
    expectedDoNCount: number;
    timeEstimate: TimeEstimateT;
    bestScore?: Score;
    latestScore?: Score;
    mastery: MasteryLevel;
    masteryEnabled: boolean;
    problemTypeKind: "GROUPED" | "UNGROUPED";
};

export type Article = CommonContentItem & {
    kind: "Article";
};

export type CourseChallenge = CommonContentItem & {
    id: string;
    kind: "CourseChallenge";
};

export type AIGuideActivity = CommonContentItem & {
    id: string;
    kind: "AIGuideActivity";
};

export type Scratchpad = CommonContentItem & {
    kind: "Scratchpad";
    category:
        | "tutorial"
        | "official"
        | "documentation"
        | "sanctioned"
        | "challenge"
        | "project";
};

export type Challenge = CommonContentItem & {
    kind: "Challenge";
};

export type Talkthrough = CommonContentItem & {
    kind: "Talkthrough";
};

export type Project = CommonContentItem & {
    kind: "Project";
};

export type Interactive = CommonContentItem & {
    kind: "Interactive";
};

export type ScoreMap = Record<string, Score>;

export type ContentItem =
    | Video
    | Exercise
    | Article
    | Scratchpad
    | Challenge
    | Talkthrough
    | Project
    | Interactive;

export const getContentDescriptor = (contentItem: ContentItem): string => {
    return contentItem.kind + ":" + contentItem.contentId;
};

export interface ContentItemProgress {
    // NOTE(yejia): Graphene Enums get uppercased. This should be ItemStatus.
    completionStatus: "UNSTARTED" | "STARTED" | "COMPLETE";
    content: ContentItem;
}

export const SUBJECT_CHALLENGE_QUIZ_TYPE = "subject-challenge";
export type QuizType =
    | "quiz"
    | "test"
    | "subject-challenge"
    | "mastery-challenge";

// TODO(benkraft, LP-4473): This type is used for the quiz object at many
// different points on its journey to be augmented with various user and topic
// data.  In many cases, these fields aren't optional; rather we know where in
// the code they get added, or to which quiz types they apply.  Clean it all
// up!  Use different types!  Be clear about which data exists where!
interface InterspersedQuizCommon {
    kind: "InterspersedQuiz";
    domain: DomainSlug;
    // The number of items that will be included in the quiz/test. Note that,
    // in practice, this number could differ from the actual length of the
    // task, if we were to publish between the time that we serve this page and
    // the time that the user opens the quiz/test.
    numItems: number;
    // I'm not certain if `numItems` above is deprecated, but `exerciseLength`
    // is definitely what should be used in some cases.
    exerciseLength?: number;
    timeEstimate: TimeEstimateT;
    // undefined if quiz is incomplete, or not yet fetched
    score?: Score;
    slug: string;
    subjectTitle?: string;
    // Not a fully qualified url, but a relative path.  E.g. /math/early-math/
    subjectPath?: string;
    title: string;
    topic: {
        id: string;
        slug: string;
        title: string;
        masteryEnabled: boolean;
    };
    tutorials: Array<QuizTutorial>;
    // TODO(benkomalo): probably cleaner if the "interspersed quizzes" and
    // summative "unit test" were separated out more explicitly, and delegated
    // shared parts to a common component.
    // Required if type == 'quiz'
    positionKey?: string;
    positionSlug?: string;
    quizIndex?: number;
    // undefined if quiz data is not yet fetched
    canResume?: boolean;
    // Course Challenges don't exist in the topic tree, so they don't have
    // identifiers or descriptors.
    // Unfortunately rewriting this type as a disjoint union introduces all
    // kinds of TypeScript errors so that will have to be a TODO(slim).
    type: QuizType;
    contentDescriptor: string | null | undefined;
    contentId: string | null | undefined;
    // Thus far only defined on ContentItems but added here to make TypeScript happy.
    nodePath?: ContentNodePath;
}

export type QuizTestChallengeT = InterspersedQuizCommon;

export type MasteryChallengeBaseT = InterspersedQuizCommon & {
    canResume: false;
    timeLeftSeconds: null;
    expirationTime: null;
    expirationPeriodHours: null;
};

export type MasteryChallengeCanResumeT = InterspersedQuizCommon & {
    canResume: true;
    timeLeftSeconds: number;
    expirationTime: string;
    expirationPeriodHours: number;
};

export type MasteryChallengeT =
    | MasteryChallengeBaseT
    | MasteryChallengeCanResumeT;

export type InterspersedQuizT = QuizTestChallengeT | MasteryChallengeT;

export interface UserAttemptData {
    id: string;
    numAttempted: number;
    numCorrect: number;
    completedDate: string;
    canResume: boolean;
    isCompleted: boolean;
    // positionKey is included only for quiz attempt data.
    // TODO(benkraft): Is there a good way to tell TypeScript that?
    positionKey?: string;
}

export type ContentItemKinds =
    | "Video"
    | "Exercise"
    | "Article"
    | "Scratchpad"
    | "Challenge"
    | "Talkthrough"
    | "Project"
    | "Interactive";

// cf. content_render._module_sigs._SUBJECT_CHALLENGE_TUTORIAL_SIG
// TODO(benkraft, LP-4473): This sig actually represents a bunch of different things --
// various wrappers augment it with their own data.  Split each up into its own
// type to make it clear which fields exist where.
export interface QuizTutorialContentItem {
    kind: ContentItemKinds;
    contentId: string;
    // TODO(benkraft): The server doesn't send down "nodeUrl" --
    // where the heck does it come from?  Is it even set/used?
    nodeUrl?: string;
    // Added by mapStateToProps in topic-progress.jsx
    mastery?: MasteryLevel;
    // Added by augmentWithUserInfoState in quiz-utils.js
    recommended?: boolean;
    title: string;
    translatedTitle?: string;
    // TODO(benkraft): This may be required -- it's used by getModalItemExtra
    // in modal-navigator-store.js and the data-sources are way too hard for me
    // to trace, so I'm leaving it as optional for now.
    slug?: string;
}

// TODO(sophie): eventually dedupe with and merge into type Tutorial
export interface QuizTutorial {
    contentItems: Array<QuizTutorialContentItem>;
    id: string;
    slug: string;
    title: string;
    url: string;
}

export interface QuizDescription {
    scoreText: string | null | undefined;
    dateText: string | null | undefined;
    copy: string | null | undefined;
}

export type ModalItem = ContentItem | InterspersedQuizT;

/**
 * A recommended content item on a topic study guide page.
 */
export interface Recommendation {
    contentId: string;
    // e.g. the quiz that recommended this item
    sourceId: string;
    kind: string;
}

export interface Attempt {
    correct?: boolean;
    seenHint?: boolean;
}

// TODO(kevinb): include finish_video and reach_end_of_video
export type Criterion =
    | {
          type: "num_problems";
          numRequired: number;
      }
    | {
          type: "num_correct_in_a_row";
          numRequired: number;
      }
    | {
          type: "first_x_or_num_correct_in_a_row";
          numRequired: number;
          first: number;
      }
    | {
          type: "x_of_the_last_y";
          numRequired: number;
          correct: number;
      };

export interface LegacyExerciseModel {
    displayName: string;
    translatedDisplayName?: string;
}
